import { ROUTES } from "@/app/routes";
import { CiShare1 } from "react-icons/ci";
import { FcCancel } from "react-icons/fc";
import { RegisterOptions, useForm } from "react-hook-form";
import { useEffect, useState } from "react";
import { useUtils } from "@/app/hooks/useUtils";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import Input from "@/app/components/ui/Inputs/Input";
import RBACFlag from "@/app/components/core/RBACFlag";
import { PhoneIcon } from "@heroicons/react/24/outline";
import { IoCheckmarkCircleOutline } from "react-icons/io5";
import { useAppDispatch } from "@/app_redux/hooks/root_hook";
import useFormValidations from "@/app/hooks/useFormValidations";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import RefundRejectModalComponent from "./RefundRejectModalComponent";
import { IDebitNoteFilter } from "@/api_framework/api_modals/payment";
import { useSelectMenuReducer } from "@/app/hooks/useSelectMenuReducer";
import OMCalendarComponent from "@/app/components/ui/Calendar/OMCalendar";
import NoItemFoundComponent from "@/app/components/ui/NoItemFoundComponent";
import SearchableMultiSelectMenu from "@/app/components/ui/SelectMenus/MultiSelectMenu";
import { ICartState, setRefundRequestFilter } from "@/app_redux/reducers/slices/auth/cart_slice";
import { usePaymentDebitStatusListAPI } from "@/app/hooks/api_hooks/payment/usePaymentDebitStatusListAPI";
import { usePaymentRefundRequestListAPI } from "@/app/hooks/api_hooks/payment/usePaymentRefundRequestListAPI";

export interface InvoiceFilter {
  status: Array<string>;
  user: string;
}

const RefundRequestComponent = () => {
  const { getRouteKey } = useRouter();
  const { veticDateFormat, statusBackgroundColor } = useUtils();
  const dispatch = useAppDispatch();
  const { NO_CART } = useImageConstants();
  const { refundRequestList } = useRouteInfo(getRouteKey("CART_MANAGEMENT", "id")).routeState.state as ICartState;
  const isTransactionLoading = useLoader("isCustomerRefundRequestTableInProgress");
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 30);
  const [selectionDate, setSelectionDate] = useState({
    startDate: yesterday,
    endDate: today,
    key: "selectionDate",
  });
  const startDate = new Date(selectionDate.startDate);
  const endDate = new Date(selectionDate.endDate);
  const startYear = startDate.getFullYear();
  const startMonth = (startDate.getMonth() + 1).toString().padStart(2, "0");
  const startDay = startDate.getDate().toString().padStart(2, "0");
  const endYear = endDate.getFullYear();
  const endMonth = (endDate.getMonth() + 1).toString().padStart(2, "0");
  const endDay = endDate.getDate().toString().padStart(2, "0");
  const formattedStartDate = `${startDay}-${startMonth}-${startYear}`;
  const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
  const [open, setOpen] = useState(false);
  const [debitNoteReceiptId, setDebitNoteReceiptId] = useState(null);
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [initial, setInitial] = useState(false);
  const { execute: fetchRequestedApproval } = usePaymentRefundRequestListAPI();
  const { execute: fetchStatusList, debitStatus } = usePaymentDebitStatusListAPI();
  const { forAlphaNumeric } = useFormValidations();
  const debitStatusItem = useSelectMenuReducer(debitStatus, "value", "id");

  const formHook = useForm<InvoiceFilter>({
    mode: "onChange",
    defaultValues: {},
  });

  const selectedStatus = formHook.watch("status");

  useEffect(() => {
    if (selectedStatus && !initial) {
      fetchRequestedApproval(formattedStartDate, formattedEndDate, "", formHook.getValues("status")?.join(","));
      const constructedBody: IDebitNoteFilter = {
        enddate: formattedEndDate,
        startdate: formattedStartDate,
        status: formHook.getValues("status")?.join(","),
        user: "",
      };
      dispatch(setRefundRequestFilter(constructedBody));
      setInitial(true);
    }
  }, [selectedStatus, initial]);

  const onSubmit = (data: InvoiceFilter) => {
    fetchRequestedApproval(formattedStartDate, formattedEndDate, data.user, data.status?.join(","));
    const constructedBody: IDebitNoteFilter = {
      enddate: formattedEndDate,
      startdate: formattedStartDate,
      status: data.status?.join(","),
      user: formHook.getValues("user") ? formHook.getValues("user") : "",
    };
    dispatch(setRefundRequestFilter(constructedBody));
  };

  useEffect(() => {
    fetchStatusList();
  }, []);

  const handelToUserProfile = (userId: string) => {
    const url = `${ROUTES.ROOT.children.CUSTOMER_PROFILE.children.DETAILS_CUSTOMER.url}/appointments/${userId}?from_om=true`;
    window.open(url, "_self");
  };

  return (
    <div className="h-full">
      <div className="pb-0.5 rounded-lg bg-white px-2">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center border-b pb-2 gap-2">
          <p className="text-sm font-semibold mt-2">Refund Requests</p>
        </div>
        <form onSubmit={formHook.handleSubmit(onSubmit)} className="pt-2">
          <div className="flex flex-col xl:flex-row xl:items-center gap-3">
            <OMCalendarComponent
              selectionDate={selectionDate}
              setSelectionDate={setSelectionDate}
              startDate={formattedStartDate}
              endDate={formattedEndDate}
              handelClick={() => {
                // handelClick();
              }}
            />
            <div className="w-full sm:w-56">
              <SearchableMultiSelectMenu
                onSelectItem={(items) => {
                  if (items) {
                    const status = items.map((i) => i.id);
                    formHook.setValue("status", status);
                  }
                }}
                withSelectAll
                defaultSelectAll={true}
                showTooltips
                withDropDownSearch
                errorMessages={[{ message: "Required", type: "required" }]}
                fieldError={formHook.formState.errors.status}
                register={formHook.register("status", {
                  required: true,
                })}
                showTypedErrors={false}
                containerClassName="w-full"
                placeholder="Select Status"
                defaultSelected={debitStatusItem?.filter((c) => selectedStatus?.includes(c.id))}
                selectItems={debitStatusItem}
              />
            </div>
            <div className="w-full sm:w-56">
              <Input
                className=" text-xs"
                placeholder="Please Search With Mobile Number"
                maxLength={10}
                register={formHook.register("user", {
                  required: false,
                  ...(forAlphaNumeric.validations as RegisterOptions<InvoiceFilter, "user">),
                })}
                fieldError={formHook.formState.errors.user}
                errorMessages={[forAlphaNumeric.errors, { message: "Number is required", type: "required" }]}
              />
            </div>
            <button
              className="bg-black text-xs text-white font-medium rounded-md px-3 h-9 w-full sm:w-32 shrink-0"
              type="submit"
            >
              Apply
            </button>
          </div>
        </form>
      </div>
      {isTransactionLoading ? (
        <TableSkeleton />
      ) : refundRequestList?.length > 0 ? (
        <div className="mt-2 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] overflow-auto">
          <div className="w-full">
            <div className="inline-block min-w-full align-middle">
              <table className="w-full min-w-[1280px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-20">
                  <tr>
                    {tableHeader.map((key, index) => (
                      <th
                        key={index}
                        scope="col"
                        className="p-2 text-left text-xs whitespace-nowrap font-medium text-black tracking-wider"
                      >
                        {key.item}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {refundRequestList?.map((cl, index) => {
                    const isFirst = index === 0;
                    const isLast = index === refundRequestList.length - 1;
                    const isSingle = refundRequestList.length === 1;
                    return (
                      <tr
                        key={index}
                        className={`
                          bg-white
                          ${isSingle ? "rounded-xl" : ""}
                          ${isFirst && !isSingle ? "rounded-t-xl" : ""}
                          ${isLast && !isSingle ? "rounded-b-xl" : ""}
                        `}
                      >
                        <td
                          className={`
                            px-2 py-3 whitespace-nowrap text-xs font-normal
                            ${isSingle ? "rounded-tl-2xl rounded-bl-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tl-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-bl-2xl" : ""}
                          `}
                        >
                          <span className="text-xs">{cl?.name} &nbsp;</span>
                          <span className="flex items-center text-[10px]">
                            {cl?.mobile && (
                              <span className="flex flex-row items-center gap-1">
                                {cl?.mobile} <PhoneIcon className="w-3 h-3" />
                              </span>
                            )}
                          </span>
                          <p
                            className="text-[#0068FF] font-medium flex flex-row items-center gap-1 text-xs cursor-pointer"
                            onClick={() => handelToUserProfile(cl?.user_id)}
                          >
                            Customer Profile <CiShare1 className="w-4 h-4 font-semibold cursor-pointer" />
                          </p>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          {cl?.invoice_id}
                          <a
                            className="text-[#0068FF] font-medium flex flex-row items-center gap-1 text-xs cursor-pointer"
                            href={cl?.pdf_url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            View PDF <CiShare1 className="w-4 h-4 font-semibold cursor-pointer" />
                          </a>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          <button
                            className={`${statusBackgroundColor(cl?.status?.toLowerCase())} py-1 px-3 rounded-full text-[#000000] capitalize`}
                          >
                            {cl?.status}
                          </button>
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          {cl?.invoice_total}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          {Number(cl?.refund_amount ?? 0).toFixed(2)}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          {cl?.refund_request_at ? veticDateFormat(cl?.refund_request_at) : "NA"}
                        </td>
                        <td className="px-2 py-3 whitespace-nowrap text-xs font-normal text-gray-900">
                          {cl?.refund_request_by}
                        </td>
                        <td
                          className={`
                            px-2 py-3 whitespace-nowrap text-xs font-normal
                            ${isSingle ? "rounded-tr-2xl rounded-br-2xl" : ""}
                            ${isFirst && !isSingle ? "rounded-tr-2xl" : ""}
                            ${isLast && !isSingle ? "rounded-br-2xl" : ""}
                          `}
                        >
                          <RBACFlag actionId="REFUND_REQUEST_ACTION_CTA">
                            {cl?.status === "pending" && (
                              <div className="flex items-center gap-4">
                                <div
                                  className="text-[#808DA0] flex flex-col gap-2 items-center cursor-pointer"
                                  onClick={() => {
                                    setOpen(true);
                                    setApprovalStatus("Approve");
                                    setDebitNoteReceiptId(cl?.refund_request_id);
                                  }}
                                >
                                  <p>
                                    <IoCheckmarkCircleOutline className="w-4 h-4" />
                                  </p>
                                  <p>Approve</p>
                                </div>
                                <div
                                  className="text-[#E11D48] flex flex-col gap-2 items-center cursor-pointer"
                                  onClick={() => {
                                    setOpen(true);
                                    setApprovalStatus("Reject");
                                    setDebitNoteReceiptId(cl?.refund_request_id);
                                  }}
                                >
                                  <p>
                                    <FcCancel className="w-4 h-4" />
                                  </p>
                                  <p>Reject</p>
                                </div>
                              </div>
                            )}
                          </RBACFlag>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          {open && (
            <RefundRejectModalComponent
              open={open}
              setOpen={setOpen}
              invoiceId={debitNoteReceiptId}
              handelClickToFetch={() => onSubmit}
              approvalStatus={approvalStatus}
            />
          )}
        </div>
      ) : (
        refundRequestList?.length === 0 && (
          <div className="bg-white h-[400px] mt-3">
            <NoItemFoundComponent
              message=""
              description="No Refund Requests Found"
              imageURL={NO_CART}
              InputClassName="my-0 h-full"
            />
          </div>
        )
      )}
    </div>
  );
};

export default RefundRequestComponent;

const TableSkeleton = () => {
  return (
    <div className="mt-3 scroll-inner !h-auto max-h-[520px] sm:max-h-[650px] flow-root relative rounded-xl bg-[#F9FAFB] animate-pulse">
      <div className="w-full overflow-x-auto">
        <div className="inline-block min-w-full align-middle">
          <table className="w-full min-w-[1280px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
            <thead className="bg-[#F5F5F5] text-[#1F1F23] font-normal sticky top-0 z-10">
              <tr>
                {[...Array(8)].map((_, index) => (
                  <th key={index} className="p-2 py-3 text-left text-xs font-medium text-gray-300 tracking-wider">
                    <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[...Array(8)].map((_, rowIndex) => (
                <tr key={rowIndex} className="bg-white">
                  {[...Array(8)].map((_, colIndex) => (
                    <td key={colIndex} className="px-2 py-3 whitespace-nowrap text-xs text-gray-300">
                      <div className="h-3 bg-gray-200 rounded w-full"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const tableHeader = [
  { item: "Client Details" },
  { item: "Invoice ID" },
  { item: "Refund Status" },
  { item: "Invoice Amount" },
  { item: "Refund Amount" },
  { item: "Date" },
  { item: "Created By" },
  { item: "Action" },
];
